import { createHash } from 'node:crypto';
import { z } from 'zod';
import { createInterfaceId, Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { PersistentStorage } from '@gitlab-org/persistent-storage';

const STORAGE_KEY = 'pluginCommandApprovals';
const SCHEMA_VERSION = 1;

const StorageSchema = z.object({
  version: z.literal(SCHEMA_VERSION),
  approvals: z.record(
    z.string(),
    z.object({
      hash: z.string(),
      approvedAt: z.string(),
    }),
  ),
});

type ApprovalsSchema = z.infer<typeof StorageSchema>;

/** The security-relevant execution surface of a `run:` plugin command. */
export interface ApprovalSubject {
  run: string;
  interactive: boolean | undefined;
  timeoutSeconds: number | undefined;
  env: Record<string, string> | undefined;
}

/**
 * Stable digest of everything that changes what a `run:` command can do. Any
 * change (command string, env vars, interactivity, timeout) yields a new hash,
 * so a plugin update that alters the command re-triggers the approval prompt.
 */
export function computeApprovalHash(subject: ApprovalSubject): string {
  const canonical = JSON.stringify({
    run: subject.run,
    interactive: subject.interactive ?? false,
    timeoutSeconds: subject.timeoutSeconds ?? null,
    // Deterministic code-unit order — localeCompare varies with the system
    // locale, which would make the hash unstable across sessions.
    env: Object.entries(subject.env ?? {}).sort(([a], [b]) => {
      if (a < b) return -1;
      return a > b ? 1 : 0;
    }),
  });
  return createHash('sha256').update(canonical).digest('hex');
}

/**
 * Persists user approvals of plugin `run:` commands across CLI sessions.
 * Keys are `<pluginId>/<commandName>`; each key stores the hash of the
 * approved command surface. A mismatched hash (the plugin changed its
 * command) counts as not approved.
 */
export interface PluginCommandApprovalStore {
  isApproved(key: string, hash: string): Promise<boolean>;
  approve(key: string, hash: string): Promise<void>;
}

export const PluginCommandApprovalStore = createInterfaceId<PluginCommandApprovalStore>(
  'PluginCommandApprovalStore',
);

/**
 * Approval store backed by the shared {@link PersistentStorage} (0600 file
 * mode, cross-process locking), mirroring `PersistentMcpServerApprovalStore`.
 * Unreadable or corrupt storage fails closed: everything unapproved.
 */
@Implements(PluginCommandApprovalStore)
@Service({
  dependencies: [Logger, PersistentStorage],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultPluginCommandApprovalStore implements PluginCommandApprovalStore {
  #logger: Logger;

  #storage: PersistentStorage;

  // Serializes all store operations. Each is read-modify-write against a
  // single storage key, and PersistentStorage only locks individual get/set
  // calls — not the get→set sequence — so concurrent callers would otherwise
  // read the same baseline and clobber each other's writes: last write wins,
  // losing every approval but the last.
  #queue: Promise<unknown> = Promise.resolve();

  constructor(logger: Logger, storage: PersistentStorage) {
    this.#logger = withPrefix(logger, '[PluginCommandApprovalStore]');
    this.#storage = storage;
  }

  /** Run `operation` after all previously-enqueued operations have settled. */
  #enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.#queue.then(operation, operation);
    // Keep the chain alive regardless of whether `operation` resolved or
    // rejected, without surfacing earlier errors to later callers.
    this.#queue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  async isApproved(key: string, hash: string): Promise<boolean> {
    return this.#enqueue(async () => {
      try {
        const schema = await this.#readSchema();
        return schema.approvals[key]?.hash === hash;
      } catch (e) {
        // Fail closed: unreadable storage means unapproved (re-prompts).
        this.#logger.warn(
          `Could not read plugin command approvals; treating "${key}" as unapproved`,
          this.#asError(e),
        );
        return false;
      }
    });
  }

  async approve(key: string, hash: string): Promise<void> {
    await this.#enqueue(async () => {
      try {
        const schema = await this.#readSchema();
        schema.approvals[key] = { hash, approvedAt: new Date().toISOString() };
        await this.#storage.set(STORAGE_KEY, schema);
        this.#logger.info(`Persisted approval for plugin command "${key}"`);
      } catch (e) {
        // Best effort: a failed write only means the user is re-prompted next
        // session.
        this.#logger.warn(
          `Could not persist approval for plugin command "${key}"`,
          this.#asError(e),
        );
      }
    });
  }

  async #readSchema(): Promise<ApprovalsSchema> {
    const raw = await this.#storage.get(STORAGE_KEY);
    if (raw === undefined || raw === null) {
      return { version: SCHEMA_VERSION, approvals: {} };
    }
    const parsed = StorageSchema.safeParse(raw);
    if (!parsed.success) {
      // Corrupt data must fail closed: everything unapproved until re-approved.
      this.#logger.warn(`Unexpected ${STORAGE_KEY} schema — resetting; all commands unapproved`);
      return { version: SCHEMA_VERSION, approvals: {} };
    }
    return parsed.data;
  }

  #asError(e: unknown): Error {
    return e instanceof Error ? e : new Error(String(e));
  }
}
